// src/oura/components/site/AiReadersCard.tsx
//
// The AI reader test on the staff Site screen (H38; scope 3.8): one line per
// reader (ChatGPT, Claude, Perplexity), each with whether robots.txt lets
// it in, whether a fetch as that reader got in, whether the page's words
// show without scripts, and when it was checked. A3 "Let AI read your site"
// closes its AI readers item on this proof once all 3 get in; until then the
// item stays open in Delivery.
//
// The lines are Delivery's (the A3 item's readerChecks), so this card and
// the drawer never disagree. Mounted only behind DELIVERY_WORK_ON, by staff.

import { useMemo, useState } from "react";
import { BotIcon } from "lucide-react";
import { READERS_PART, UNTESTED_READERS, readerItemOf, shortDay, testAiReaders } from "@service/aeo/delivery";
import { myDate } from "@service/aeo/clientDocsRules";
import { ArrowLink, Dot, FoldCard, PillButton, SURFACE, TYPE } from "@oura/ui";
import { cn } from "../../lib/cn";
import { useDelivery } from "../../delivery/useDelivery";
import { ReaderLines } from "../../delivery/WorkParts";
import { readerPasses } from "../../delivery/workModel";

export function AiReadersCard({ businessId, businessName, onOpenDelivery }: { businessId: string; businessName: string; onOpenDelivery?: () => void }) {
    const { load } = useDelivery(businessId, businessName);
    const [busy, setBusy] = useState(false);
    const [said, setSaid] = useState<{ tone: "ok" | "error"; text: string } | null>(null);
    const hit = useMemo(() => (load.state === "ready" ? readerItemOf(load.data.deliverables, myDate(new Date())) : null), [load]);
    if (!hit) return null;

    const { d, item } = hit;
    const checks = item.readerChecks ?? [];
    const pass = checks.filter(readerPasses).length;
    const tested = checks.some((c) => c.checkedAt);
    const closed = item.state === "done";

    const test = async () => {
        setBusy(true);
        setSaid(null);
        // The button frees itself whatever happens: a payload that throws while it
        // is read must not leave "Test again" spinning until the page reloads.
        try {
            const r = await testAiReaders(businessId);
            if (!r.ok) {
                setSaid({ tone: "error", text: r.message ?? "The test did not run. Try again." });
                return;
            }
            const it = r.data.items.find((i) => i.id.endsWith(READERS_PART));
            const n = (it?.readerChecks ?? []).filter(readerPasses).length;
            setSaid({ tone: "ok", text: `Tested just now: ${n} of 3 get in.` });
        } catch {
            setSaid({ tone: "error", text: "The test did not run. Try again." });
        } finally {
            setBusy(false);
        }
    };

    return (
        <FoldCard
            id="ai-readers"
            anchor="ai-readers"
            className="scroll-mt-6"
            title="AI readers"
            icon={{ icon: BotIcon, area: "site" }}
            foldedFact={tested ? `${pass} of 3 get in` : "Not tested"}
            right={<span>3 readers</span>}
        >
            <ReaderLines checks={checks.length ? checks : UNTESTED_READERS} />
            <div className={cn(SURFACE.inner, "mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2")}>
                <p className={cn(TYPE.tc, "m-0 flex min-w-0 flex-1 basis-56 items-start gap-2 text-gray-900")}>
                    <Dot tone={closed ? "green" : "blue"} className="mt-[5px]" />
                    <span className="min-w-0">
                        <span className="font-medium">
                            {d.code} {d.label}
                        </span>
                        <span className="text-gray-600">
                            {closed
                                ? `: the AI readers item closed on ${item.proof?.kind === "scan" && item.proof.at ? `the test of ${shortDay(myDate(item.proof.at))}` : "its proof"}.`
                                : ": the AI readers item closes when all 3 get in."}
                        </span>
                    </span>
                </p>
                <span className="flex shrink-0 items-center gap-3">
                    {onOpenDelivery && <ArrowLink onClick={onOpenDelivery}>Open Delivery</ArrowLink>}
                    <PillButton size="sm" busy={busy} writes onClick={() => void test()}>
                        {tested ? "Test again" : "Test the AI readers"}
                    </PillButton>
                </span>
            </div>
            {said && (
                <p role="status" className={cn(TYPE.tc, "m-0 mt-2", said.tone === "error" ? "text-rose-600" : "text-gray-600")}>
                    {said.text}
                </p>
            )}
        </FoldCard>
    );
}
